import { useState, useEffect, } from 'react';
import { useParams, useNavigate, Form } from 'react-router-dom';
import { calculateFinancialSummary, 
  formatCurrency, 
  getVal, 
  isNegative, 
} from '../../helpers/process_morningstar.js';
import { StrategyContext } from "../Context/StrategyContext.js";
import { format, isValid, subMonths, subDays, set } from "date-fns";
import axios from "axios";
import pLimit from "p-limit";

import {
  Box,
  Container,
  CircularProgress,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableHead,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
} from "@mui/material";

import { 
  CartesianGrid,
  Cell,
  Bar, 
  BarChart, 
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  LabelList 
} from 'recharts';

const WEBAPI = process.env.REACT_APP_WEB_API;

const headers = [
    "Overall",
    "Return on Capital",
    "Balance Sheet",
    "Growth",
    "Valuation",   
]

const CAExchanges = ["TSX", "V", "TSE", "CNQ"];

function backgroundColor(value) {
  // Handle invalid or missing values
  if ((value) === null || value === undefined || value === "") return "#ffffffff"; // gray fallback for bad input

  const colorRanges = [
    "#ff0000", // 0–10
    "#ff6a00ff", // 11–20
    "#a87c1c", // 21–30
    "#948222", // 31–40
    "#768c2b", // 41–50
    "#679030", // 51–60
    "#589534", // 61–70
    "#3f9c3c", // 71–80
    "#18a848", // 81–90
    "#0bac4c", // 91–100
  ];

  // Clamp the value between 0–100
  const clamped = Math.min(Math.max(value, 0), 100);

  // Find which range it's in
  const index = Math.min(Math.floor(clamped / 10), colorRanges.length - 1);

  return colorRanges[index];
}

function formatValue(v) {
  const format = Number(v).toFixed(1) + "%";
  return format;
}

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
};

const responsiveStyle = {
  whiteSpace: "normal",
  width: "auto",
  maxWidth: '160px',
  wordBreak:"break-word",
}

export default function Scorecard({ scorecardLoaded }) {
    const { ticker } = useParams()
    // const { name, market, sectors, marketCapMin, marketCapMax, factors, topN } = useContext(StrategyContext);
    const [data, setData] = useState([]);
    const [period, setPeriod] = useState("");
    const [periodList, setPeriodList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [topNCompanies, setTopNCompanies] = useState([]);
    const [tables, setTables] = useState([]);
    const [error, setError] = useState(null);
    const [scorecardData, setScorecardData] = useState([]);
    const [Tax, setTax] = useState(getVal("selectedTax"));
    const [capRD, setCapRD] = useState(getVal("capRD"));
    const [includeOtherIncome, setIncludeOtherIncome] = useState(getVal("includeOtherIncome"));
    const [barData, setBarData] = useState([])

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "capRD") setCapRD(JSON.parse(event.newValue));
      if (event.key === "includeOtherIncome") setIncludeOtherIncome(JSON.parse(event.newValue));
    };

    const handleCustomUpdate = () => {
      setCapRD(getVal("capRD"));
      setIncludeOtherIncome(getVal("includeOtherIncome"));
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("localStorageUpdated", handleCustomUpdate);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("localStorageUpdated", handleCustomUpdate);
    };
  }, []);  

   useEffect(() => {
 
     setLoading(true);
     setPeriod(""); // We need this. If we don't have it the Period UseEffect won't fire. Load a strategy and leave on the first period. Change the Strategy and the table will not change.
 
     const periodsListTmp = [];
     const startDate = new Date();
     let periodValue = 0;
 
     for (var x = 0; x < 10; x++) {
       let nextDate = subMonths(startDate, x * 3);
       while (!isValid(nextDate)) {
         subDays(nextDate, 1);
       }
 
       periodsListTmp.push({ value: String(periodValue).padStart(2, "0"), label: format(nextDate, "MMM dd, yyyy") });
       //console.log(periodsListTmp);
       periodValue++;
     }
    //periodsListTmp.reverse();
     setPeriodList(periodsListTmp);

     setPeriod("00");
   }, []);

useEffect(() => {
  const fetchScorecardData = async () => {
    if (!periodList?.length || !ticker) return;
    setLoading(true);

    const exchange = ticker.split(".")[1].toUpperCase();
    const isCA = CAExchanges.includes(exchange);

    const limit = pLimit(5); // Limit to 5 concurrent requests

    const FIXED_PERIODS = ["00","01","02","03","04","05","06","07","08","09"];
    const validPeriod = (val) => FIXED_PERIODS.includes(val) ? val : "00";

    const strategy = isCA 
      ? '3C4DDA34-0205-F111-812C-002590759A74'
      : '8E31B88B-0105-F111-812C-002590759A74'

    const getStrategy = await axios.get(`${WEBAPI}/quickstrategy/${strategy}`)
    console.log(getStrategy.data)
    //console.log(getStrategy.data.factors.map(obj => obj.weight).join(","))

    const payload = {
      market: getStrategy.data.market,
      sectors: getStrategy.data.sectors,
      mcap_min: getStrategy.data.marketcap_min,
      mcap_max: getStrategy.data.marketcap_max,
      metrics: getStrategy.data.factors.map(obj => obj.metric).join(","),
      ranks: getStrategy.data.factors.map(obj => obj.rank).join(","),
      weights: getStrategy.data.factors.map(obj => obj.weight).join(","),
      period: "00,01,02,03,04,05,06,07,08,09",
      tableColumns: getStrategy.data.factors.map(obj => obj.tableColumn).join(","),
      minimums: getStrategy.data.factors.map(obj => obj.minimum).join(","),
      maximums: getStrategy.data.factors.map(obj => obj.maximum).join(","),
    }

    console.log(payload)
    const promises = periodList.map((period) =>
      limit(async () => {
        const periodVal = validPeriod(period.value);

        const res = await axios.post(`${WEBAPI}/quickstrategy/results`, {
          ...payload,
          period: periodVal,
        });

        console.log('Scorecard response:', res.data);

        const stock = res.data.find(
          (obj) => obj.Ticker === ticker.toUpperCase()
        );
        if (!stock) return null;

        return {
          id: periodVal,
          period: period.label.substring(0, 12),
          overall: stock.OverallRank * 100,
          cfirrExTfq: stock.CFIRREX_TTM_PctRank * 100,
          cfirrInTfq: stock.CFIRR_TTM_PctRank * 100,
          cfirrExChgTfqVsPq: stock.CFIRREXCHG_TTMvPQ_PctRank * 100,
          cfirrInChgTfqVsPq: stock.CFIRRCHG_TTMvPY_PctRank * 100,
          totInflAdj: stock.IAGFACHG_ANQvPY_PctRank * 100,
          currentRatio: stock.CURRENTRATIO_ANQ_PctRank * 100,
          revGrw: stock.REVENUECHG_TTMvPY_PctRank * 100,
          basicCFoverGrw: stock.BASICCFCHG_TTMvPY_PctRank * 100,
          evOverEbitda: stock.EVEBITDA_ANQ_PctRank * 100,
          pbook: stock.PBOOK_ANQ_PctRank * 100,
        };
      })
    );

    const results = await Promise.all(promises);

    // sort by id (period.value) after all requests finish
    const sortedData = results.filter(Boolean).sort((a, b) => a.id.localeCompare(b.id));
    const selectedData = sortedData[0]
    
    if(selectedData == null || selectedData === undefined) {
      setBarData([
        { name: "Overall", value: 0 },
        { name: "", isHeader: true },

        { name: "Return on Capital", isHeader: true },
        { name: "CFIRR Ex % (TFQ)", value: 0 },
        { name: "CFIRR In % (TFQ)", value: 0 },
        { name: "CFIRR Ex Chg %", value: 0 },
        { name: "CFIRR In Chg %", value: 0 },
        { name: "", isHeader: true },

        { name: "Balance Sheet", isHeader: true },
        { name: "Capital Grw % (LFQ)", value: 0 },
        { name: "Current Ratio (LFQ)", value: 0 },
        { name: "", isHeader: true },

        { name: "Growth", isHeader: true },
        { name: "Rev Grw %", value: 0 },
        { name: "CFPS %", value: 0 },
        { name: "", isHeader: true },

        { name: "Valuation", isHeader: true },
        { name: "EV / EBITDA (TFQ)", value: 0 },
        { name: "P / Book (LFQ)", value: 0 },
      ])
    } else {
      setBarData([
        { name: "Overall", value: selectedData.overall },
        { name: "", isHeader: true },

        { name: "Return on Capital", isHeader: true },
        { name: "CFIRR Ex % (TFQ)", value: selectedData.cfirrExTfq },
        { name: "CFIRR In % (TFQ)", value: selectedData.cfirrInTfq },
        { name: "CFIRR Ex Chg %", value: selectedData.cfirrExChgTfqVsPq },
        { name: "CFIRR In Chg %", value: selectedData.cfirrInChgTfqVsPq },
        { name: "", isHeader: true },

        { name: "Balance Sheet", isHeader: true },
        { name: "Capital Grw % (LFQ)", value: selectedData.totInflAdj },
        { name: "Current Ratio (LFQ)", value: selectedData.currentRatio },
        { name: "", isHeader: true },

        { name: "Growth", isHeader: true },
        { name: "Rev Grw %", value: selectedData.revGrw },
        { name: "CFPS %", value: selectedData.basicCFoverGrw },
        { name: "", isHeader: true },

        { name: "Valuation", isHeader: true },
        { name: "EV / EBITDA (TFQ)", value: selectedData.evOverEbitda },
        { name: "P / Book (LFQ)", value: selectedData.pbook },
      ])
    }
    console.log(selectedData)

    setScorecardData(sortedData);
    setLoading(false);
    scorecardLoaded(true);
  };


  fetchScorecardData();
}, [periodList, ticker]);

  const handlePeriodChange = (event) => {
    const newPeriod = event.target.value
    setPeriod(newPeriod)

    setBarData([
      { name: "Overall", value: scorecardData[Number(newPeriod)].overall },
      { name: "", isHeader: true },

      { name: "Return on Capital", isHeader: true },
      { name: "CFIRR Ex % (TFQ)", value: scorecardData[Number(newPeriod)].cfirrExTfq },
      { name: "CFIRR In % (TFQ)", value: scorecardData[Number(newPeriod)].cfirrInTfq },
      { name: "CFIRR Ex Chg %", value: scorecardData[Number(newPeriod)].cfirrExChgTfqVsPq },
      { name: "CFIRR In Chg %", value: scorecardData[Number(newPeriod)].cfirrInChgTfqVsPq },
      { name: "", isHeader: true },

      { name: "Balance Sheet", isHeader: true },
      { name: "Capital Grw % (LFQ)", value: scorecardData[Number(newPeriod)].totInflAdj },
      { name: "Current Ratio (LFQ)", value: scorecardData[Number(newPeriod)].currentRatio },
      { name: "", isHeader: true },

      { name: "Growth", isHeader: true },
      { name: "Rev Grw %", value: scorecardData[Number(newPeriod)].revGrw },
      { name: "CFPS %", value: scorecardData[Number(newPeriod)].basicCFoverGrw },
      { name: "", isHeader: true },

      { name: "Valuation", isHeader: true },
      { name: "EV / EBITDA (TFQ)", value: scorecardData[Number(newPeriod)].evOverEbitda },
      { name: "P / Book (LFQ)", value: scorecardData[Number(newPeriod)].pbook },
    ])

  }

  //console.log(barData)
    return (
      <Box>
        <Paper sx={{
          height: 360,    
          padding: 1,
          borderRadius: 2,
        }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%"
          }}
        >
          <FormControl size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '0px' } }}>
            <InputLabel></InputLabel>
            <Select 
              value={period} 
              onChange={handlePeriodChange}
              sx={{ fontSize: '0.8rem' }}
              >
              {periodList
                .filter((period) => scorecardData[Number(period.value)])
                .map((period) => (
                <MenuItem 
                  key={period.value} 
                  value={period.value}
                  sx={{ fontSize: '0.8rem' }}
                  >
                  {period.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
            <Typography sx={{ fontWeight: 'bold' }}>Percentile Ranking</Typography>
          </Box>
          
          <Box sx={{ width: '120px' }}></Box>
        </Box>
        <ResponsiveContainer  width="100%" height={300}>
          <BarChart data={barData} layout="vertical" >
            <CartesianGrid 
              horizontal={false}      // Disable horizontal lines
              vertical={true}         // Enable vertical lines
            />
            <XAxis 
              type="number" 
              domain={[0, 100]} 
              tickFormatter={(value) => `${value}%`}
              fontSize="10px"
              />
            <YAxis 
              dataKey="name" 
              type="category"
              width={110}
              interval={0}
              tick={({ x, y, payload }) => {
                const isHeader = barData[payload.index]?.isHeader;
                const isOverall = barData[payload.index]?.name === "Overall";


                return (
                  <text
                    x={x}
                    y={y}
                    dy={4}
                    textAnchor="end"
                    fontWeight={isHeader || isOverall ? "bold" : "normal"}
                    fontSize={isHeader || isOverall ? "12px" : "9px"}
                  >
                    {isOverall ? (
                      <>
                        <tspan x={x} dy={0}>Overall</tspan>
                        <tspan x={x} dy="12" fontWeight={'normal'} fontSize={"9px"}>(Weighted Averages)</tspan>
                      </>
                    ) : (
                      <tspan x={x} dy={4}>{payload.value}</tspan>     
                    )}
                  </text>
                );
              }}
               />
            <Tooltip
            content={({ payload }) => {
              if (!payload || payload.length === 0) return null;

              const data = payload[0].payload; // graphData item
              // console.log(data)

              return (
                <div
                  style={{
                    backgroundColor: "rgba(50, 50, 50, 0.6)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    borderRadius: "6px",
                    padding: "6px",
                    color: "#fff",
                    backdropFilter: "blur(4px)",
                  }}
                >
                  <div>{data.name}</div>
                  <div>{(data.value).toFixed(0)}%</div>
                </div>
                );
              }}
             />
            <Bar
              dataKey="value"
              isAnimationActive={false}
              shape={(props) => {
                const { payload } = props;

                if (payload.isHeader) return null;

                return <rect {...props} />
              }}
            >
              {barData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.name === "Overall" ? "#F6BB00" : "#FFE699"}  
                />
              ))}
              <>
                {/* Values ≥ 5 → insideRight */}
                <LabelList
                  dataKey="value"
                  position="insideRight"
                  formatter={(v) => (v >= 5 ? `${v.toFixed(0)}%` : "")}
                  fontSize="10px"
                  fontWeight="bold"
                  fill="#000000"
                />

                {/* Values < 5 → outsideRight */}
                <LabelList
                  dataKey="value"
                  position="right"
                  formatter={(v) => (v < 5 ? `${v.toFixed(0)}%` : "")}
                  fontSize="10px"
                  fontWeight="bold"
                  fill="#000000"
                />
              </>
            </Bar> 
          </BarChart>
        </ResponsiveContainer>
        </Paper>
      </Box>
    );    
}